/**
 * Team & Capacity Planner v2 — Phase 4
 * /admin/capacity
 *
 * Layout:
 *   ┌── Sticky top: capacity cards (4/6/8) + Prev/Current/Next  ─────┐
 *   │  ⚠ WEEK 30  |  CURRENT W31  |  WEEK 32  |  W33  ...            │
 *   └───────────────────────────────────────────────────────────────┘
 *   Smartsheet-style planning table below:
 *     Week# | Task | Start | Finish | Revised | Workers | Days | WD | Status | Team | Comments | ⚠
 *
 * Design: Nordic industrial — navy + cyan.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Plus, Settings2, Users, X, AlertTriangle, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  TrendingUp, CheckCircle2, Trash2, Edit3, Zap, Package, Info, Filter,
  Save, ArrowLeft, ArrowRight, Layers, Target, ClockAlert, Calendar, Upload,
  ListPlus, CalendarOff, Sun, Moon, Umbrella, Columns3, Eye, EyeOff, Check, RefreshCw,
} from "lucide-react";
import QuickAddRowModal from "./capacity/QuickAddRowModal";
import WeekOverrideModal from "./capacity/WeekOverrideModal";
import { DEFAULT_COLUMN_ORDER, normalizeColumnOrder, sanitizeHiddenColumns } from "./capacity/columns";
import { TASK_COLUMN_DEFS } from "./capacity/columns";
import MobilePlanningList from "./capacity/MobilePlanningList";
import { useIsMobile } from "@/hooks/useIsMobile";
import { PermissionGate, usePermissions } from "@/context/PermissionContext";
import AbsencesModal from "./capacity/AbsencesModal";
import { isoWeekNow, dateToIsoWeek, businessDaysBetween, weeksInRange, WeekBadge } from "./capacity/shared";
import { CAPACITY_THEME_CSS, getThemePreference } from "./capacity/theme";
import {
  STATUS_META, STATUS_BUCKET_STYLE, TEAM_COLOR,
  inputCls, authHeader, fmtDate, statusOptions,
} from "./capacity/constants";
import { useEscapeKey, CapacityBar, Field, StatusPill } from "./capacity/atoms";
import PlanningTable from "./capacity/PlanningTable";
import DoserneTopNav from "./DoserneTopNav";
import ChatFab from "../doserne/ChatFab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/capacity`;

/* ============================================================
   Week Capacity Card  (Phase 4 — richer)
============================================================ */
function WeekCapacityCard({ week, isSelected, onClick, onAdjust }) {
  const style = STATUS_BUCKET_STYLE[week.modes.expected.status] || STATUS_BUCKET_STYLE.available;
  const pct = week.percent_worker_days || week.modes.expected.percent || 0;
  const isPastUnfinished = week.is_past && week.overdue_projects > 0;
  const isCurrent = week.is_current;
  const carryCount = week.carry_over_count || 0;
  const hasOverride = !!week.override;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick && onClick(); }}
      data-testid={`week-card-${week.week}`}
      className={`relative text-left rounded-2xl border p-4 transition group cursor-pointer
        ${isSelected ? "border-cyan-400 ring-2 ring-cyan-400/40 bg-cyan-500/5" : "border-white/10 hover:border-white/20"}
        ${isPastUnfinished ? "bg-red-500/5 border-red-500/40" : "bg-[#0f1a2e]"}
        ${isCurrent ? "ring-1 ring-cyan-400/40" : ""}
        ${hasOverride ? "border-amber-400/60" : ""}
      `}
    >
      {/* Gear icon — opens WeekOverrideModal */}
      {onAdjust && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onAdjust(week); }}
          data-testid={`week-adjust-${week.week}`}
          title={hasOverride ? "Edit capacity override" : "Adjust capacity (holiday, short week)"}
          className={`absolute top-2 right-2 p-1.5 rounded-lg transition
            ${hasOverride ? "text-amber-300 bg-amber-500/10 hover:bg-amber-500/20"
                          : "text-slate-500 opacity-0 group-hover:opacity-100 hover:bg-white/10 hover:text-amber-300"}`}
        >
          <CalendarOff className="w-3.5 h-3.5"/>
        </button>
      )}

      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-1.5">
            {isPastUnfinished && <AlertTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />}
            <span className={`text-[10px] uppercase tracking-widest font-black ${isCurrent ? "text-cyan-300" : "text-slate-400"}`}>
              {isCurrent ? "CURRENT WEEK" : week.week}
            </span>
            {isCurrent && carryCount > 0 && (
              <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] font-black text-amber-300">
                <AlertTriangle className="w-3 h-3" /> {carryCount}
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">{week.label}</div>
        </div>
        <div className="text-right pr-6">
          <div className="text-2xl font-black text-slate-100 leading-none">{pct}%</div>
          <div className={`text-[10px] uppercase font-black tracking-widest mt-0.5 ${style.chip.split(" ")[1]}`}>
            {style.label}
          </div>
        </div>
      </div>

      <CapacityBar pct={pct} bucketStatus={week.modes.expected.status} />

      <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-3 text-[10px]">
        <div className="text-slate-500">Worker-days</div>
        <div className="text-right text-slate-200 font-mono">
          {week.worker_days_used?.toFixed(0) || 0}/{week.worker_days_capacity || 0}
        </div>
        <div className="text-slate-500">Available workers</div>
        <div className="text-right text-slate-200 font-mono">{week.estimated_available_workers || 0}</div>
        <div className="text-slate-500">Active projects</div>
        <div className="text-right text-slate-200 font-mono">{week.active_projects || 0}</div>
        {week.overdue_projects > 0 && (
          <>
            <div className="text-red-400">Overdue</div>
            <div className="text-right text-red-300 font-mono font-black">{week.overdue_projects}</div>
          </>
        )}
      </div>

      {hasOverride && (
        <div className="mt-2 pt-2 border-t border-amber-400/20 text-[10px] text-amber-200 flex items-center gap-1 truncate"
             title={week.override.note}>
          <CalendarOff className="w-3 h-3 flex-shrink-0"/>
          {week.override.workers_off > 0 && <span>−{week.override.workers_off} workers</span>}
          {week.override.teams_off?.length > 0 && <span>· {week.override.teams_off.length} teams off</span>}
          {week.override.working_days !== null && week.override.working_days !== undefined && (
            <span>· {week.override.working_days}d week</span>
          )}
        </div>
      )}

      {week.absences?.length > 0 && (
        <div className="mt-2 pt-2 border-t border-emerald-400/20 text-[10px] text-emerald-200 flex items-center gap-1 truncate"
             title={week.absences.map((a) => `${a.worker_name} (${a.absence_type}, ${a.days_in_week}d)`).join(" · ")}
             data-testid={`week-absences-${week.week}`}>
          <Umbrella className="w-3 h-3 flex-shrink-0"/>
          <span>{week.absences.length} on leave · −{week.absence_worker_days}wd</span>
        </div>
      )}

      {isCurrent && carryCount > 0 && (
        <div className="mt-2 pt-2 border-t border-white/5 text-[10px] text-amber-300 flex items-center gap-1">
          <ClockAlert className="w-3 h-3" />
          {carryCount} carry-over · {week.carry_over_worker_days?.toFixed(0) || 0} wd
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Capacity Ribbon — sticky bar of week cards + navigation
============================================================ */
function CapacityRibbon({
  weeksData, weekCount, setWeekCount, startWeek, setStartWeek,
  selectedWeek, setSelectedWeek, mode, setMode, onAdjustWeek,
  fullYear, setFullYear,
}) {
  const isMobile = useIsMobile();
  const goPrev = () => {
    if (!weeksData || !weeksData.weeks.length) return;
    const first = weeksData.weeks.find((w) => !w.is_past) || weeksData.weeks[0];
    // Shift back by `weekCount` from the visible non-past start
    const [yy, ww] = first.week.split("-W").map(Number);
    let newWeek = ww - weekCount;
    let newYear = yy;
    while (newWeek <= 0) { newWeek += 52; newYear -= 1; }
    setStartWeek(`${newYear}-W${String(newWeek).padStart(2, "0")}`);
  };
  const goNext = () => {
    if (!weeksData || !weeksData.weeks.length) return;
    const last = weeksData.weeks[weeksData.weeks.length - 1];
    const [yy, ww] = last.week.split("-W").map(Number);
    let newWeek = ww + 1;
    let newYear = yy;
    while (newWeek > 52) { newWeek -= 52; newYear += 1; }
    setStartWeek(`${newYear}-W${String(newWeek).padStart(2, "0")}`);
  };
  const goCurrent = () => setStartWeek("");

  // Weekly capacity cards start collapsed — most users focus on the task list
  // and only need the metrics ribbon occasionally. Preference is remembered
  // per browser via localStorage.
  const [cardsOpen, setCardsOpen] = React.useState(() => {
    try { return localStorage.getItem("capacity_ribbon_open") === "1"; } catch { return false; }
  });
  const toggleCards = () => setCardsOpen((v) => {
    const nx = !v;
    try { localStorage.setItem("capacity_ribbon_open", nx ? "1" : "0"); } catch { /* ignore */ }
    return nx;
  });

  return (
    <div className="border-b border-white/5 bg-[#0a1628]/95 backdrop-blur-lg sticky top-[120px] z-20">
      <div className="max-w-[1600px] mx-auto px-4 py-3">
        {/* Controls row */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <button onClick={goPrev}
                  data-testid="nav-prev"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-xs font-black uppercase">
            <ChevronLeft className="w-3.5 h-3.5"/> Previous
          </button>
          <button onClick={goCurrent}
                  data-testid="nav-current"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-cyan-500/20 border border-cyan-400/50 text-cyan-200 hover:bg-cyan-500/30 text-xs font-black uppercase">
            <Calendar className="w-3.5 h-3.5"/> Current
          </button>
          <button onClick={goNext}
                  data-testid="nav-next"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-xs font-black uppercase">
            Next <ChevronRight className="w-3.5 h-3.5"/>
          </button>
          <div className="ml-2 flex items-center gap-1 p-1 rounded-lg bg-white/5 border border-white/10">
            {[4, 6, 8].map((n) => (
              <button key={n} onClick={() => { setWeekCount(n); setFullYear?.(false); }}
                      data-testid={`week-selector-${n}`}
                      className={`px-2.5 py-1 rounded text-[10px] uppercase tracking-widest font-black transition
                        ${!fullYear && weekCount === n ? "bg-cyan-500 text-slate-900" : "text-slate-400 hover:text-slate-100"}`}>
                {n} weeks
              </button>
            ))}
            <button onClick={() => setFullYear?.(!fullYear)}
                    data-testid="week-selector-year"
                    title="Show every ISO week of the current year"
                    className={`px-2.5 py-1 rounded text-[10px] uppercase tracking-widest font-black transition
                      ${fullYear ? "bg-cyan-500 text-slate-900" : "text-slate-400 hover:text-slate-100"}`}>
              Full year
            </button>
          </div>
          <div className="ml-auto flex items-center gap-1 p-1 rounded-lg bg-white/5 border border-white/10">
            {["confirmed", "expected", "worst_case"].map((m) => (
              <button key={m} onClick={() => setMode(m)}
                      data-testid={`mode-${m}`}
                      className={`px-3 py-1 rounded text-[10px] uppercase tracking-widest font-black transition
                        ${mode === m ? "bg-cyan-500 text-slate-900" : "text-slate-400 hover:text-slate-100"}`}>
                {m.replace("_", " ")}
              </button>
            ))}
          </div>
          <button onClick={toggleCards}
                  data-testid="btn-toggle-ribbon"
                  title={cardsOpen ? "Hide weekly cards" : "Show weekly cards"}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-[10px] uppercase tracking-widest font-black">
            {cardsOpen ? <ChevronUp className="w-3.5 h-3.5"/> : <ChevronDown className="w-3.5 h-3.5"/>}
            {cardsOpen ? "Skjul" : "Vis"} uker
          </button>
        </div>

        {/* Cards row */}
        {cardsOpen && weeksData?.weeks?.length ? (
          <div className="grid gap-3 overflow-x-auto pb-2"
               style={{ gridTemplateColumns: isMobile
                 ? `repeat(${weeksData.weeks.length}, 200px)`
                 : `repeat(${weeksData.weeks.length}, minmax(0, 1fr))` }}>
            {(() => {
              // Reorder cards: CURRENT week first (left), then FUTURE ascending,
              // then PAST descending (most recent past just after future).
              // This gives quick access to "today" and forward planning while keeping
              // recent history at hand.
              const ws = weeksData.weeks;
              const cur = ws.find((w) => w.is_current);
              const fut = ws.filter((w) => !w.is_current && !w.is_past)
                            .sort((a, b) => (a.week < b.week ? -1 : 1));
              const past = ws.filter((w) => w.is_past)
                             .sort((a, b) => (a.week < b.week ? 1 : -1));
              const ordered = [...(cur ? [cur] : []), ...fut, ...past];
              return ordered.map((w) => (
                <WeekCapacityCard key={w.week} week={w}
                                  isSelected={selectedWeek === w.week}
                                  onClick={() => {
                                    // Filter to this week AND scroll the table to it.
                                    setSelectedWeek(selectedWeek === w.week ? null : w.week);
                                    setTimeout(() => {
                                      const el = document.querySelector(`[data-testid="week-group-header-${w.week}"]`);
                                      if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
                                    }, 50);
                                  }}
                                  onAdjust={onAdjustWeek} />
              ));
            })()}
          </div>
        ) : cardsOpen ? (
          <div className="text-center py-8 text-slate-500 text-xs">
            <Loader2 className="w-4 h-4 animate-spin mx-auto"/> Loading weeks...
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ============================================================
   Carry-Over modal (management updates revised plan)
============================================================ */
function CarryOverModal({ project, onClose, onSaved }) {
  useEscapeKey(onClose);
  const [form, setForm] = useState({
    planned_workers_remaining: project?.carry_over?.planned_workers_remaining ?? "",
    remaining_working_days: project?.carry_over?.remaining_working_days ?? "",
    revised_expected_finish: project?.revised_expected_finish || "",
    carry_over_reason: project?.carry_over?.carry_over_reason || "",
    carry_over_status: project?.carry_over?.carry_over_status || "pending_review",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        planned_workers_remaining: form.planned_workers_remaining === "" ? null : parseInt(form.planned_workers_remaining),
        remaining_working_days: form.remaining_working_days === "" ? null : parseFloat(form.remaining_working_days),
        revised_expected_finish: form.revised_expected_finish || null,
        carry_over_reason: form.carry_over_reason,
        carry_over_status: form.carry_over_status,
      };
      await axios.post(`${API}/projects/${project.id}/carry-over`, payload, authHeader());
      toast.success("Carry-over plan saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const rwd = (parseInt(form.planned_workers_remaining) || 0) * (parseFloat(form.remaining_working_days) || 0);
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0a1628] overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-black flex items-center gap-1">
              <ClockAlert className="w-3 h-3"/> Update carry-over
            </div>
            <h2 className="text-lg font-black text-slate-100">{project.name}</h2>
            <div className="text-[11px] text-slate-400 mt-1">
              Original finish: <span className="text-slate-200">{fmtDate(project.original_expected_finish)}</span>
              {" · "}
              Status: <StatusPill status={project.status}/>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-100"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Workers still required" hint="How many workers continue on this job">
              <input type="number" min="0" value={form.planned_workers_remaining}
                     onChange={(e) => setForm({ ...form, planned_workers_remaining: e.target.value })}
                     className={inputCls} data-testid="co-workers"/>
            </Field>
            <Field label="Remaining working days">
              <input type="number" step="0.5" min="0" value={form.remaining_working_days}
                     onChange={(e) => setForm({ ...form, remaining_working_days: e.target.value })}
                     className={inputCls} data-testid="co-days"/>
            </Field>
            <Field label="Revised expected finish" hint="Original date will be preserved">
              <div className="flex items-center gap-1">
                <input type="date" value={form.revised_expected_finish}
                       onChange={(e) => setForm({ ...form, revised_expected_finish: e.target.value })}
                       className={inputCls} data-testid="co-revised"/>
                <WeekBadge value={form.revised_expected_finish} testId="co-revised-week"/>
              </div>
            </Field>
            <Field label="Carry-over status">
              <select value={form.carry_over_status} onChange={(e) => setForm({ ...form, carry_over_status: e.target.value })} className={inputCls}>
                <option value="pending_review">Pending review</option>
                <option value="revised">Revised</option>
                <option value="confirmed_finish">Finish confirmed</option>
              </select>
            </Field>
          </div>
          <Field label="Reason for delay">
            <textarea rows={3} value={form.carry_over_reason}
                      onChange={(e) => setForm({ ...form, carry_over_reason: e.target.value })}
                      className={inputCls}
                      placeholder="Weather, material delay, scope change..."
                      data-testid="co-reason"/>
          </Field>
          <div className="rounded-xl bg-cyan-500/10 border border-cyan-400/30 p-3">
            <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">Remaining worker-days</div>
            <div className="text-3xl font-black text-slate-100 mt-1">{rwd.toFixed(1)}</div>
            <div className="text-[10px] text-slate-400">Will be added to current & future weeks</div>
          </div>
        </div>
        <div className="px-6 py-3 border-t border-white/10 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-white/10 text-slate-300 text-xs font-black uppercase">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="co-save"
                  className="px-5 py-2 rounded-lg bg-cyan-500 text-slate-900 text-xs font-black uppercase flex items-center gap-1">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <><Save className="w-3.5 h-3.5"/> Save</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   CSV Import modal
============================================================ */
function ImportModal({ onClose, onSaved }) {
  useEscapeKey(onClose);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/import/smartsheet-csv`, fd, {
        ...authHeader(),
        headers: { ...authHeader().headers, "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.imported} projects (${data.skipped} skipped)`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0a1628] p-6 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-black">Import Smartsheet · CSV or Excel</div>
          <h2 className="text-lg font-black text-slate-100">Import projects</h2>
        </div>
        <div className="rounded-xl border border-dashed border-white/20 p-4 bg-white/5 text-center">
          <Upload className="w-6 h-6 text-cyan-400 mx-auto mb-2"/>
          <input type="file" accept=".csv,.xlsx,.xlsm" onChange={(e) => setFile(e.target.files?.[0])}
                 className="text-xs text-slate-300 w-full" data-testid="import-file"/>
          {file && <div className="mt-2 text-[11px] text-slate-400">{file.name} · {(file.size/1024).toFixed(1)} KB</div>}
        </div>
        <div className="text-[10px] text-slate-500 leading-relaxed">
          Accepts <span className="text-cyan-300 font-black">.csv</span> or <span className="text-cyan-300 font-black">.xlsx</span> files.
          Columns recognised: Task Name, Client, Date expected to start, Date expected to finish,
          Members, Status, Comments, Week Number. Status values map automatically
          (Not Started → not_started, In Progress → in_progress, Noted → noted, Waiting for answer → waiting_for_customer,
          Complete/Completed → completed, Cancelled → cancelled, Paused → paused).
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-white/10 text-slate-300 text-xs font-black uppercase">Cancel</button>
          <button onClick={submit} disabled={!file || busy} data-testid="import-submit"
                  className="px-5 py-2 rounded-lg bg-cyan-500 disabled:opacity-40 text-slate-900 text-xs font-black uppercase flex items-center gap-1">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <><Upload className="w-3.5 h-3.5"/> Import</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Wizard (retained from Phase 2 — status list widened)
============================================================ */
const emptyProject = {
  name: "", client: "", address: "", work_type: "facade",
  priority: "normal", status: "not_started",
  requested_start: "", notes: "", comments: "", color: "cyan",
  assigned_team: "",
  original_expected_start: "", original_expected_finish: "",
  revised_expected_finish: "", actual_finish: "",
  estimate: {
    assembly_m2: 0, dismantling_m2: 0, estimated_height: 0, carrying_distance: 0,
    stair_towers: 0, transport_trips: 0,
    complexity: "normal", site_conditions: "normal",
    teams_required: 1, workers_per_team: 4,
    productivity_target: 8.0, productive_hours_per_day: 7.5,
    manager_budget_hours_override: null, manager_planned_duration_days: null,
    buffer_percentage: 0.10, estimate_notes: "", estimate_status: "draft",
  },
};

function ProjectWizard({ initial, onClose, onSaved }) {
  useEscapeKey(onClose);
  const [form, setForm] = useState(() => initial ? { ...emptyProject, ...initial, estimate: { ...emptyProject.estimate, ...(initial.estimate || {}) } } : emptyProject);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;
  const setEst = (patch) => setForm((f) => ({ ...f, estimate: { ...f.estimate, ...patch } }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      if (isEdit) await axios.put(`${API}/projects/${initial.id}`, payload, authHeader());
      else await axios.post(`${API}/projects`, payload, authHeader());
      toast.success(isEdit ? "Updated" : "Created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const steps = [
    { n: 1, label: "Project", Icon: Package },
    { n: 2, label: "Dates & scope", Icon: Calendar },
    { n: 3, label: "Team & estimate", Icon: Layers },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-3xl max-h-[92vh] rounded-2xl overflow-hidden border border-white/10 bg-[#0a1628] flex flex-col">
        <div className="px-6 py-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-black">{isEdit ? "Edit" : "New project"}</div>
              <h2 className="text-xl font-black text-slate-100 mt-0.5">{isEdit ? initial.name : "Plan a new project"}</h2>
            </div>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-100" data-testid="wizard-close"><X className="w-5 h-5"/></button>
          </div>
          <div className="mt-4 flex gap-1">
            {steps.map((s) => (
              <button key={s.n} onClick={() => setStep(s.n)} data-testid={`wizard-step-${s.n}`}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black
                        ${step === s.n ? "bg-cyan-500 text-slate-900" : "bg-white/5 text-slate-400 hover:text-slate-100"}`}>
                <s.Icon className="w-3.5 h-3.5"/>{s.n}. {s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name *"><input required value={form.name} data-testid="wiz-name"
                     onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls}/></Field>
              <Field label="Client"><input value={form.client}
                     onChange={(e) => setForm({ ...form, client: e.target.value })} className={inputCls}/></Field>
              <Field label="Address"><input value={form.address}
                     onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls}/></Field>
              <Field label="Assigned team (display)"><input value={form.assigned_team}
                     onChange={(e) => setForm({ ...form, assigned_team: e.target.value })} className={inputCls}/></Field>
              <Field label="Status"><select value={form.status} data-testid="wiz-status"
                     onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                {statusOptions(form.status).map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
              </select></Field>
              <Field label="Priority"><select value={form.priority}
                     onChange={(e) => setForm({ ...form, priority: e.target.value })} className={inputCls}>
                {["low","normal","high","urgent"].map((x) => <option key={x} value={x}>{x}</option>)}
              </select></Field>
              <div className="col-span-2">
                <Field label="Comments (visible in table)">
                  <textarea rows={2} value={form.comments}
                            onChange={(e) => setForm({ ...form, comments: e.target.value })} className={inputCls}/>
                </Field>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Original expected start *">
                <div className="flex items-center gap-1">
                  <input type="date" value={form.original_expected_start} data-testid="wiz-start"
                         onChange={(e) => setForm({ ...form, original_expected_start: e.target.value })} className={inputCls}/>
                  <WeekBadge value={form.original_expected_start} testId="wiz-start-week"/>
                </div>
              </Field>
              <Field label="Original expected finish *">
                <div className="flex items-center gap-1">
                  <input type="date" value={form.original_expected_finish} data-testid="wiz-finish"
                         onChange={(e) => setForm({ ...form, original_expected_finish: e.target.value })} className={inputCls}/>
                  <WeekBadge value={form.original_expected_finish} testId="wiz-finish-week"/>
                </div>
              </Field>
              <Field label="Revised expected finish" hint="Filled when the project is delayed. Original date NEVER overwritten.">
                <div className="flex items-center gap-1">
                  <input type="date" value={form.revised_expected_finish}
                         onChange={(e) => setForm({ ...form, revised_expected_finish: e.target.value })} className={inputCls}/>
                  <WeekBadge value={form.revised_expected_finish} testId="wiz-revised-week"/>
                </div>
              </Field>
              <Field label="Actual finish (when done)">
                <div className="flex items-center gap-1">
                  <input type="date" value={form.actual_finish}
                         onChange={(e) => setForm({ ...form, actual_finish: e.target.value })} className={inputCls}/>
                  <WeekBadge value={form.actual_finish} testId="wiz-actual-week"/>
                </div>
              </Field>
              <div className="col-span-2 grid grid-cols-2 gap-3">
                {[["assembly_m2","Assembly m²"], ["dismantling_m2","Dismantling m²"]].map(([k,l]) => (
                  <Field key={k} label={l}>
                    <input type="number" min="0" value={form.estimate[k]}
                           onChange={(e) => setEst({ [k]: parseFloat(e.target.value) || 0 })} className={inputCls}/>
                  </Field>
                ))}
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Teams required">
                <input type="number" min="1" value={form.estimate.teams_required} data-testid="wiz-teams"
                       onChange={(e) => setEst({ teams_required: parseInt(e.target.value) || 1 })} className={inputCls}/>
              </Field>
              <Field label="Workers per team">
                <input type="number" min="1" value={form.estimate.workers_per_team}
                       onChange={(e) => setEst({ workers_per_team: parseInt(e.target.value) || 1 })} className={inputCls}/>
              </Field>
              <Field label="Productivity (m²/h/pers)">
                <input type="number" step="0.1" min="0" value={form.estimate.productivity_target}
                       onChange={(e) => setEst({ productivity_target: parseFloat(e.target.value) || 0 })} className={inputCls}/>
              </Field>
              <Field label="Manager planned duration (days)">
                <input type="number" step="0.5" min="0" placeholder="auto" data-testid="wiz-planned"
                       value={form.estimate.manager_planned_duration_days ?? ""}
                       onChange={(e) => setEst({ manager_planned_duration_days: e.target.value === "" ? null : parseFloat(e.target.value) })}
                       className={inputCls}/>
              </Field>
              <Field label="Estimate status">
                <select value={form.estimate.estimate_status}
                        onChange={(e) => setEst({ estimate_status: e.target.value })} className={inputCls}>
                  <option value="draft">Draft (needs review)</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="approved">Approved</option>
                </select>
              </Field>
            </div>
          )}
        </div>
        <div className="border-t border-white/10 px-6 py-3 flex items-center justify-between">
          <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}
                  className="text-xs uppercase font-black text-slate-400 disabled:opacity-30 flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5"/> Prev
          </button>
          <div className="text-[10px] uppercase font-black text-slate-500">Step {step}/3</div>
          {step < 3 ? (
            <button onClick={() => setStep(step + 1)}
                    className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-slate-100 text-xs uppercase font-black flex items-center gap-1">
              Next <ArrowRight className="w-3.5 h-3.5"/>
            </button>
          ) : (
            <button onClick={submit} disabled={busy || !form.name}
                    data-testid="wiz-save"
                    className="px-5 py-2 rounded-lg bg-cyan-500 text-slate-900 text-xs uppercase font-black flex items-center gap-1 disabled:opacity-40">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <><Save className="w-3.5 h-3.5"/> {isEdit ? "Save" : "Create"}</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Settings + Teams modals — simplified re-use
============================================================ */
function SettingsModal({ settings, onClose, onSaved }) {
  useEscapeKey(onClose);
  const [form, setForm] = useState(settings);
  const [busy, setBusy] = useState(false);
  const setW = (k, v) => setForm((f) => ({ ...f, status_weights: { ...f.status_weights, [k]: v } }));
  const setColor = (k, v) => setForm((f) => ({
    ...f,
    status_colors: { ...(f.status_colors || {}), [k]: v },
  }));
  const clearColor = (k) => setForm((f) => {
    const c = { ...(f.status_colors || {}) };
    delete c[k];
    return { ...f, status_colors: c };
  });
  const submit = async () => {
    setBusy(true);
    try { await axios.put(`${API}/settings`, form, authHeader()); toast.success("Saved"); onSaved(); }
    catch { toast.error("Failed"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[92vh] rounded-2xl border border-white/10 bg-[#0a1628] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-lg font-black text-slate-100">Settings</h2>
          <button onClick={onClose} className="p-2 text-slate-400"><X className="w-5 h-5"/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Working days/week"><input type="number" min="1" max="7" value={form.working_days_per_week} data-testid="set-wdays"
              onChange={(e) => setForm({ ...form, working_days_per_week: parseInt(e.target.value) || 5 })} className={inputCls}/></Field>
            <Field label="Default workers/team"><input type="number" min="1" value={form.default_workers_per_team}
              onChange={(e) => setForm({ ...form, default_workers_per_team: parseInt(e.target.value) || 4 })} className={inputCls}/></Field>
            <Field label="Productive hours/day"><input type="number" step="0.25" min="0" value={form.productive_hours_per_day}
              onChange={(e) => setForm({ ...form, productive_hours_per_day: parseFloat(e.target.value) || 7.5 })} className={inputCls}/></Field>
            <Field label="Default productivity"><input type="number" step="0.1" min="0" value={form.default_productivity}
              onChange={(e) => setForm({ ...form, default_productivity: parseFloat(e.target.value) || 8 })} className={inputCls}/></Field>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black mb-2">Status weights</div>
            <div className="grid grid-cols-2 gap-1.5">
              {Object.keys(STATUS_META).map((s) => (
                <div key={s} className="flex items-center gap-2 p-2 rounded bg-white/5 border border-white/10">
                  <StatusPill status={s}/>
                  <input type="number" step="0.1" min="0" max="1" value={form.status_weights?.[s] ?? 0}
                         onChange={(e) => setW(s, parseFloat(e.target.value) || 0)}
                         className={`ml-auto w-16 ${inputCls} text-center`} data-testid={`weight-${s}`}/>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black">Status colors (Smartsheet-style row tint)</div>
              <div className="text-[10px] text-slate-500">Click a colour to pick, or × to reset.</div>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {Object.keys(STATUS_META).map((s) => {
                const c = form.status_colors?.[s] || "";
                return (
                  <div key={s} className="flex items-center gap-2 p-2 rounded bg-white/5 border border-white/10"
                       style={c ? { backgroundColor: `${c}22` } : undefined}
                       data-testid={`color-row-${s}`}>
                    <StatusPill status={s}/>
                    <div className="ml-auto flex items-center gap-1.5">
                      <input type="color" value={c || "#334155"}
                             onChange={(e) => setColor(s, e.target.value)}
                             className="w-8 h-6 rounded cursor-pointer border border-white/10 bg-transparent"
                             data-testid={`color-${s}`}
                             title={c ? `Custom: ${c}` : "Click to set custom colour"}/>
                      {c && (
                        <button type="button" onClick={() => clearColor(s)}
                                className="p-1 rounded text-slate-500 hover:text-red-300"
                                data-testid={`color-reset-${s}`}
                                title="Reset to default">
                          <X className="w-3 h-3"/>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black">Current-week frame colour</div>
              <div className="text-[10px] text-slate-500">The colour that frames the current week block in the planning table.</div>
            </div>
            <div className="flex items-center gap-3 p-2 rounded bg-white/5 border border-white/10">
              <div className="text-xs font-black text-slate-200">Frame colour</div>
              <input type="color"
                     value={form.current_week_frame_color || "#10b981"}
                     onChange={(e) => setForm((cur) => ({ ...cur, current_week_frame_color: e.target.value }))}
                     className="w-10 h-7 rounded cursor-pointer border border-white/10 bg-transparent"
                     data-testid="current-week-frame-color"/>
              <span className="text-[10px] font-mono text-slate-400">{form.current_week_frame_color || "#10b981"}</span>
              <button type="button"
                      onClick={() => setForm((cur) => ({ ...cur, current_week_frame_color: "#10b981" }))}
                      className="ml-auto text-[10px] uppercase tracking-widest font-black text-slate-400 hover:text-emerald-300">
                Reset (emerald)
              </button>
            </div>
          </div>

          <div className="rounded-xl border border-red-500/40 bg-red-500/5 p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-red-300 font-black flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3"/> Danger zone
            </div>
            <div className="text-[11px] text-slate-300 leading-relaxed">
              Delete ALL projects and schedules in one shot. Useful after a bad import.
              Teams, settings, colors and week overrides are preserved.
            </div>
            <button type="button"
                    onClick={async () => {
                      const s1 = window.prompt('This will DELETE every project + schedule. Type DELETE_ALL to confirm:');
                      if (s1 !== 'DELETE_ALL') { toast.error('Cancelled'); return; }
                      try {
                        const { data } = await axios.post(`${API}/admin/reset-projects?confirm=DELETE_ALL`, {}, authHeader());
                        toast.success(`Deleted ${data.deleted_projects} project(s) + ${data.deleted_schedules} schedule(s)`);
                        onSaved();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'Reset failed');
                      }
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-400 text-white text-[10px] uppercase tracking-widest font-black"
                    data-testid="reset-all-projects">
              <Trash2 className="w-3 h-3"/> Delete ALL projects
            </button>
          </div>
        </div>
        <div className="px-6 py-3 border-t border-white/10 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-white/10 text-slate-300 text-xs font-black uppercase">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="set-save"
                  className="px-5 py-2 rounded-lg bg-cyan-500 text-slate-900 text-xs font-black uppercase flex items-center gap-1">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <><Save className="w-3.5 h-3.5"/> Save</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function TeamsModal({ teams, onClose, reload }) {
  useEscapeKey(onClose);
  const [form, setForm] = useState({ name: "", team_leader: "", standard_size: 4, active: true, base_location: "", color: "cyan", members: [] });
  const [memberName, setMemberName] = useState("");
  const [editing, setEditing] = useState(null);
  const isEdit = !!editing;
  useEffect(() => { if (editing) setForm(editing); }, [editing]);
  const addMember = () => {
    if (!memberName.trim()) return;
    setForm((f) => ({ ...f, members: [...f.members, { name: memberName.trim(), role: "worker", phone: "", skills: [], photo_url: "" }] }));
    setMemberName("");
  };
  const submit = async () => {
    try {
      if (isEdit) await axios.put(`${API}/teams/${editing.id}`, form, authHeader());
      else await axios.post(`${API}/teams`, form, authHeader());
      toast.success("Saved"); setEditing(null); reload();
      setForm({ name: "", team_leader: "", standard_size: 4, active: true, base_location: "", color: "cyan", members: [] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try { await axios.delete(`${API}/teams/${t.id}`, authHeader()); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const toggle = async (t) => { try { await axios.put(`${API}/teams/${t.id}`, { ...t, active: !t.active }, authHeader()); reload(); } catch { toast.error("Failed"); } };
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl max-h-[92vh] rounded-2xl border border-white/10 bg-[#0a1628] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-lg font-black text-slate-100">Teams ({teams.length})</h2>
          <button onClick={onClose} className="p-2 text-slate-400"><X className="w-5 h-5"/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {teams.map((t) => (
              <div key={t.id} className={`flex items-center gap-2 p-3 rounded-lg border ${t.active ? "border-white/10 bg-white/5" : "border-white/5 bg-white/[0.02] opacity-60"}`}
                   data-testid={`team-row-${t.id}`}>
                <span className={`w-3 h-3 rounded-full ${TEAM_COLOR[t.color] || "bg-slate-400"}`}/>
                <div className="flex-1"><div className="text-sm font-black text-slate-100">{t.name}</div>
                <div className="text-[10px] text-slate-400">{t.base_location || "—"} · {t.members?.length || t.standard_size}</div></div>
                <button onClick={() => toggle(t)} className={`text-[10px] px-2 py-1 rounded ${t.active ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-700 text-slate-400"}`}>{t.active ? "On" : "Off"}</button>
                <button onClick={() => setEditing(t)} className="p-1.5 text-slate-400 hover:text-cyan-300"><Edit3 className="w-3.5 h-3.5"/></button>
                <button onClick={() => remove(t)} className="p-1.5 text-red-400"><Trash2 className="w-3.5 h-3.5"/></button>
              </div>
            ))}
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name *"><input value={form.name} data-testid="team-name"
                onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls}/></Field>
              <Field label="Standard size"><input type="number" min="1" value={form.standard_size}
                onChange={(e) => setForm({ ...form, standard_size: parseInt(e.target.value) || 4 })} className={inputCls}/></Field>
              <Field label="Team leader"><input value={form.team_leader}
                onChange={(e) => setForm({ ...form, team_leader: e.target.value })} className={inputCls}/></Field>
              <Field label="Base location"><input value={form.base_location}
                onChange={(e) => setForm({ ...form, base_location: e.target.value })} className={inputCls}/></Field>
              <Field label="Colour">
                <select value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className={inputCls}>
                  {Object.keys(TEAM_COLOR).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>
            <div className="space-y-1">
              {form.members.map((m, i) => (
                <div key={i} className="flex items-center gap-2 px-2 py-1 rounded bg-white/5 text-xs">
                  <span className="flex-1 text-slate-200">{m.name}</span>
                  <button onClick={() => setForm((f) => ({ ...f, members: f.members.filter((_, idx) => idx !== i) }))}
                          className="text-red-400"><X className="w-3 h-3"/></button>
                </div>
              ))}
              <div className="flex gap-2">
                <input value={memberName} placeholder="Add member"
                       onChange={(e) => setMemberName(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addMember())}
                       className={inputCls} data-testid="member-input"/>
                <button onClick={addMember} className="px-3 rounded-lg bg-cyan-500 text-slate-900 text-xs font-black uppercase">Add</button>
              </div>
            </div>
            <div className="flex justify-end">
              <button onClick={submit} disabled={!form.name.trim()} data-testid="team-save"
                      className="px-4 py-2 rounded-lg bg-cyan-500 text-slate-900 text-xs font-black uppercase flex items-center gap-1 disabled:opacity-40">
                <Save className="w-3.5 h-3.5"/> {isEdit ? "Save" : "Add"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Alerts panel — compact
============================================================ */
function AlertsPanel({ alerts, onJumpToWeek }) {
  // Always start collapsed. Users can expand per session; state is NOT persisted.
  const [collapsed, setCollapsed] = useState(true);
  const toggle = () => setCollapsed((v) => !v);
  if (!alerts?.length) return null;
  const highCount = alerts.filter((a) => a.severity === "high").length;
  const medCount = alerts.filter((a) => a.severity === "medium").length;
  return (
    <div className="rounded-xl border border-white/10 bg-[#0f1a2e] p-3">
      <button type="button" onClick={toggle}
              data-testid="alerts-toggle"
              className="w-full flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-black hover:text-slate-100 transition">
        <TrendingUp className="w-3 h-3"/>
        <span>Alerts ({alerts.length})</span>
        {highCount > 0 && <span className="text-red-300">· {highCount} high</span>}
        {medCount > 0 && <span className="text-amber-300">· {medCount} medium</span>}
        <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${collapsed ? "-rotate-90" : ""}`}/>
      </button>
      {!collapsed && (
        <div className="space-y-1.5 mt-2" data-testid="alerts-list">
          {alerts.map((a, i) => {
            const cls = a.severity === "high" ? "bg-red-500/10 border-red-500/30 text-red-200 hover:bg-red-500/20"
              : a.severity === "medium" ? "bg-amber-500/10 border-amber-500/30 text-amber-200 hover:bg-amber-500/20"
              : "bg-cyan-500/10 border-cyan-500/30 text-cyan-200 hover:bg-cyan-500/20";
            const Icon = a.severity === "high" ? AlertTriangle : a.severity === "medium" ? Package : Info;
            const clickable = !!a.week && !!onJumpToWeek;
            const Wrapper = clickable ? "button" : "div";
            return (
              <Wrapper key={i} type={clickable ? "button" : undefined}
                       onClick={clickable ? () => onJumpToWeek(a.week) : undefined}
                       className={`w-full text-left border rounded-lg p-2 transition ${cls} ${clickable ? "cursor-pointer" : ""}`}
                       data-testid={`alert-${a.type}`}
                       title={clickable ? `Click to jump to ${a.week}` : undefined}>
                <div className="flex items-start gap-1.5">
                  <Icon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"/>
                  <div className="text-[11px] font-black flex-1">{a.message}</div>
                  {clickable && <span className="text-[9px] uppercase tracking-widest opacity-60">→ Jump</span>}
                </div>
                {a.detail && <div className="text-[10px] mt-0.5 opacity-80 pl-5">{a.detail}</div>}
              </Wrapper>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   MAIN PAGE
============================================================ */
export default function CapacityPlanner() {
  return (
    <PermissionGate pageKey="admin_capacity" level="view">
      <CapacityPlannerInner/>
    </PermissionGate>
  );
}

function CapacityPlannerInner() {
  const [teams, setTeams] = useState([]);
  const [projects, setProjects] = useState([]);
  const [weeksData, setWeeksData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(true);
  const [weekCount, setWeekCount] = useState(6);
  const [startWeek, setStartWeek] = useState("");   // "" = current view (default)
  const [mode, setMode] = useState("expected");
  const [fullYear, setFullYear] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const [showFinished, setShowFinished] = useState(true);
  const [wizardInitial, setWizardInitial] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showTeams, setShowTeams] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showAbsences, setShowAbsences] = useState(false);
  const [carryOverProject, setCarryOverProject] = useState(null);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [overrideWeek, setOverrideWeek] = useState(null);
  // Row selection + clipboard for Ctrl+C / Ctrl+V / Ctrl+X (Smartsheet-style)
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [clipboardRow, setClipboardRow] = useState(null); // { row, cut: bool }
  // User-defined column order (drag-to-reorder table headers). Persisted locally.
  const COL_ORDER_KEY = "capacity_column_order_v1";
  const [columnOrder, setColumnOrder] = useState(() => {
    try {
      const raw = localStorage.getItem(COL_ORDER_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return normalizeColumnOrder(parsed);
      }
    } catch { /* noop */ }
    return DEFAULT_COLUMN_ORDER;
  });
  const reorderColumns = (newOrder) => {
    const norm = normalizeColumnOrder(newOrder);
    setColumnOrder(norm);
    try { localStorage.setItem(COL_ORDER_KEY, JSON.stringify(norm)); } catch { /* noop */ }
  };
  const resetColumnOrder = () => {
    setColumnOrder(DEFAULT_COLUMN_ORDER);
    try { localStorage.removeItem(COL_ORDER_KEY); } catch { /* noop */ }
    toast.success("Column order reset");
  };
  // User-defined hidden columns (declutter the grid). Persisted locally.
  // The frozen `week` column can never be hidden.
  const COL_HIDDEN_KEY = "capacity_hidden_columns_v1";
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(COL_HIDDEN_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return sanitizeHiddenColumns(parsed);
      }
    } catch { /* noop */ }
    return [];
  });
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const persistHidden = (next) => {
    const s = sanitizeHiddenColumns(next);
    setHiddenColumns(s);
    try { localStorage.setItem(COL_HIDDEN_KEY, JSON.stringify(s)); } catch { /* noop */ }
  };
  const toggleColumn = (id) => {
    if (id === "week") return; // frozen
    persistHidden(hiddenColumns.includes(id) ? hiddenColumns.filter((x) => x !== id) : [...hiddenColumns, id]);
  };
  const showAllColumns = () => persistHidden([]);
  const DRAFTS_KEY = "capacity_drafts_v1";
  const AUTOSAVE_KEY = "capacity_autosave_v1";

  // Hydrate drafts from localStorage
  const [draftRows, setDraftRows] = useState(() => {
    try {
      const raw = window.localStorage.getItem(DRAFTS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [autoSave, setAutoSave] = useState(() => {
    try { return window.localStorage.getItem(AUTOSAVE_KEY) === "true"; }
    catch { return false; }
  });
  const [bulkSaving, setBulkSaving] = useState(false);

  // Persist drafts on every change
  useEffect(() => {
    try { window.localStorage.setItem(DRAFTS_KEY, JSON.stringify(draftRows)); }
    catch { /* ignore quota */ }
  }, [draftRows]);

  useEffect(() => {
    try { window.localStorage.setItem(AUTOSAVE_KEY, String(autoSave)); }
    catch { /* ignore */ }
  }, [autoSave]);

  // Theme (persisted per browser)
  const themePref = useMemo(() => getThemePreference(), []);
  const [theme, setTheme] = useState(themePref.initial);
  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    themePref.persist(next);
  };

  const insertEmptyRow = (targetWeek) => {
    const defaultWeek = targetWeek || selectedWeek || weeksData?.current_week || isoWeekNow();
    // Compute Monday of the target week to pre-fill expected_start
    let mondayIso = "";
    const m = /^(\d{4})-W(\d{2})$/.exec(defaultWeek);
    if (m) {
      const year = parseInt(m[1], 10);
      const week = parseInt(m[2], 10);
      const jan4 = new Date(Date.UTC(year, 0, 4));
      const jan4Day = jan4.getUTCDay() || 7;
      const monday = new Date(jan4);
      monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1 + (week - 1) * 7);
      mondayIso = monday.toISOString().slice(0, 10);
    }
    const draft = {
      id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      week: defaultWeek,
      task_name: "",
      address: "",
      expected_start: mondayIso,
      expected_finish: "",
      planned_workers: 0,
      working_days: 0,
      total_hours: "",
      status: "not_started",
      assigned_team: "",
      comments: "",
    };
    setDraftRows((cur) => [draft, ...cur]);
    if (targetWeek) toast.success(`New row on ${targetWeek}`);
  };

  const updateDraft = (id, patch) => {
    setDraftRows((cur) => cur.map((d) => d.id === id ? { ...d, ...patch } : d));
  };

  const discardDraft = (id) => {
    setDraftRows((cur) => cur.filter((d) => d.id !== id));
  };

  /** Drop-target handler: user drags a draft row onto a week-group header.
   *  Sets the draft's week + Monday-of-week expected_start (if empty).
   *  Preserves any dates the user already typed. */
  const moveDraftToWeek = (draftId, targetWeek) => {
    const m = /^(\d{4})-W(\d{2})$/.exec(targetWeek);
    if (!m) return;
    const year = parseInt(m[1], 10);
    const week = parseInt(m[2], 10);
    // ISO week Monday = date.fromisocalendar equivalent in JS
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const jan4Day = jan4.getUTCDay() || 7;
    const monday = new Date(jan4);
    monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1 + (week - 1) * 7);
    const mondayIso = monday.toISOString().slice(0, 10);
    setDraftRows((cur) => cur.map((d) => {
      if (d.id !== draftId) return d;
      const patch = { week: targetWeek };
      if (!d.expected_start) patch.expected_start = mondayIso;
      return { ...d, ...patch };
    }));
    toast.success(`Draft moved to ${targetWeek}`);
  };

  const saveDraft = async (draft) => {
    if (!draft.task_name.trim()) { toast.error("Task name is required"); return; }
    if (!draft.week.trim()) { toast.error("Week is required"); return; }
    if (draft.expected_start && draft.expected_finish && draft.expected_finish < draft.expected_start) {
      toast.error("Finish date must be after start date");
      return;
    }
    try {
      // Look up existing project by name (case-insensitive) — append schedule if match
      const matched = projects.find((p) => (p.name || "").toLowerCase() === draft.task_name.trim().toLowerCase());
      let projectId = matched?.id;

      if (!projectId) {
        const { data: created } = await axios.post(`${API}/projects`, {
          name: draft.task_name.trim(),
          address: draft.address,
          status: draft.status,
          priority: "normal",
          comments: draft.comments,
          assigned_team: draft.assigned_team,
          original_expected_start: draft.expected_start,
          original_expected_finish: draft.expected_finish,
          estimate: {
            teams_required: 1,
            workers_per_team: Number.isFinite(parseInt(draft.planned_workers)) ? parseInt(draft.planned_workers) : 0,
            productivity_target: 8.0,
            productive_hours_per_day: 7.5,
            manager_planned_duration_days: Number.isFinite(parseFloat(draft.working_days)) ? parseFloat(draft.working_days) : 0,
            estimate_status: "draft",
          },
        }, authHeader());
        projectId = created.id;
      }

      const workers = Number.isFinite(parseInt(draft.planned_workers)) ? parseInt(draft.planned_workers) : 0;
      const days = Number.isFinite(parseFloat(draft.working_days)) ? parseFloat(draft.working_days) : 0;

      // If start + finish span multiple ISO weeks, create one schedule per week
      // with a proportional share of working_days.
      const spans = (draft.expected_start && draft.expected_finish)
        ? weeksInRange(draft.expected_start, draft.expected_finish)
        : [];
      const weekAllocations = spans.length > 1
        ? spans.map((s) => ({
            week: s.week,
            days: s.days,
            expected_start: s.week === spans[0].week ? draft.expected_start : "",
            expected_finish: s.week === spans[spans.length - 1].week ? draft.expected_finish : "",
          }))
        : [{
            week: draft.week.trim(),
            days,
            expected_start: draft.expected_start,
            expected_finish: draft.expected_finish,
          }];

      for (const alloc of weekAllocations) {
        await axios.post(`${API}/projects/${projectId}/schedules`, {
          week: alloc.week,
          allocated_teams: 1,
          allocated_team_days: (workers * alloc.days) / Math.max(1, workers),
          planned_workers: workers,
          working_days: alloc.days,
          total_hours: draft.total_hours !== "" && draft.total_hours != null
            ? parseFloat(draft.total_hours) || null
            : null,
          expected_start: alloc.expected_start,
          expected_finish: alloc.expected_finish,
          assigned_team_ids: [],
          is_tentative: draft.status === "tentative",
          notes: draft.comments,
        }, authHeader());
      }

      const label = weekAllocations.length > 1
        ? `${draft.task_name} → ${weekAllocations.length} weeks`
        : `${draft.task_name} → ${weekAllocations[0].week}`;
      toast.success(label);
      discardDraft(draft.id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save row");
      throw e;
    }
  };

  const isDraftValid = (d) => !!d.task_name.trim() && !!d.week.trim();
  const validDraftCount = draftRows.filter(isDraftValid).length;

  const saveAllDrafts = async () => {
    const valid = draftRows.filter(isDraftValid);
    if (valid.length === 0) { toast.error("No draft rows ready to save"); return; }
    setBulkSaving(true);
    let saved = 0, failed = 0;
    for (const d of valid) {
      try { await saveDraft(d); saved += 1; }
      catch { failed += 1; }
    }
    setBulkSaving(false);
    if (failed === 0) toast.success(`Saved ${saved} row(s)`);
    else toast.error(`Saved ${saved}, ${failed} failed`);
  };

  // Auto-save valid drafts after 3s of inactivity (only when toggle is on)
  useEffect(() => {
    if (!autoSave) return undefined;
    const valid = draftRows.filter(isDraftValid);
    if (valid.length === 0) return undefined;
    const t = setTimeout(async () => {
      for (const d of valid) {
        try { await saveDraft(d); }
        catch { /* keep the row so user can retry */ }
      }
    }, 3000);
    return () => clearTimeout(t);
  }, [draftRows, autoSave]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ count: String(weekCount), mode });
      if (startWeek) params.set("start", startWeek);
      if (fullYear) params.set("full_year", "true");
      // Always include ALL past weeks that have any schedule for historical context
      params.set("include_past_all", "true");
      const [teamsR, projectsR, weeksR, settingsR] = await Promise.all([
        axios.get(`${API}/teams`, authHeader()),
        axios.get(`${API}/projects`, authHeader()),
        axios.get(`${API}/weeks?${params.toString()}`, authHeader()),
        axios.get(`${API}/settings`, authHeader()),
      ]);
      setTeams(teamsR.data.teams || []);
      setProjects(projectsR.data.projects || []);
      setWeeksData(weeksR.data);
      setSettings(settingsR.data);
    } catch (e) { toast.error("Failed to load"); }
    setBusy(false);
  }, [weekCount, startWeek, mode, fullYear]);

  useEffect(() => { load(); }, [load]);

  // Flatten all schedule rows (one per project × week) from weeksData
  const allRows = useMemo(() => {
    if (!weeksData) return [];
    const rows = [];
    weeksData.weeks.forEach((w) => {
      (w.projects || []).forEach((sp) => {
        rows.push({ ...sp, week: w.week, is_current: !!w.is_current });
      });
    });
    return rows;
  }, [weeksData]);

  // Filter rows
  const filteredRows = useMemo(() => {
    let list = allRows;
    if (selectedWeek) list = list.filter((r) => r.week === selectedWeek);
    if (statusFilter) list = list.filter((r) => r.status === statusFilter);
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      list = list.filter((r) =>
        (r.project_name || "").toLowerCase().includes(q)
        || (r.client || "").toLowerCase().includes(q)
        || (r.address || "").toLowerCase().includes(q)
        || (r.comments || "").toLowerCase().includes(q));
    }
    if (!showFinished) {
      list = list.filter((r) => !["completed", "cancelled", "rejected"].includes(r.status));
    }
    return list;
  }, [allRows, selectedWeek, statusFilter, searchText, showFinished]);

  // Unscheduled projects (no schedule at all)
  const unscheduledRows = useMemo(() => {
    return projects
      .filter((p) => (!p.scheduled_weeks || p.scheduled_weeks.length === 0)
                     && !["completed", "cancelled", "rejected"].includes(p.status))
      .map((p) => ({
        schedule_id: `unsched-${p.id}`,
        project_id: p.id,
        project_name: p.name,
        client: p.client,
        address: p.address,
        status: p.status,
        priority: p.priority,
        expected_start: p.original_expected_start,
        expected_finish: p.original_expected_finish,
        revised_expected_finish: p.revised_expected_finish,
        planned_workers: p.estimate?.workers_per_team || 4,
        working_days: p.estimate?.calculated_duration_days || 0,
        worker_days: (p.estimate?.workers_per_team || 4) * (p.estimate?.calculated_duration_days || 0),
        assigned_team: p.assigned_team,
        comments: p.comments || p.notes || "",
        is_overdue: false,
        week: "—",
      }));
  }, [projects]);

  const editProjectById = async (projectId) => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}`, authHeader());
      setWizardInitial(data);
    } catch { toast.error("Failed to load project"); }
  };

  const openCarryOver = async (projectId) => {
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}`, authHeader());
      setCarryOverProject(data);
    } catch { toast.error("Failed"); }
  };

  const deleteSchedule = async (scheduleId) => {
    if (!window.confirm("Remove from this week?")) return;
    try {
      await axios.delete(`${API}/schedules/${scheduleId}`, authHeader());
      load();
    } catch { toast.error("Failed"); }
  };

  const quickSaveSchedule = async (payload) => {
    // Optimistic: patch the specific schedule row in-place
    setWeeksData((cur) => {
      if (!cur) return cur;
      return {
        ...cur,
        weeks: cur.weeks.map((w) => {
          if (w.week !== payload.week) return w;
          return {
            ...w,
            projects: (w.projects || []).map((p) =>
              p.schedule_id === payload.schedule_id
                ? {
                    ...p,
                    planned_workers: payload.planned_workers,
                    working_days: payload.working_days,
                    total_hours: payload.total_hours ?? null,
                    expected_start: payload.expected_start,
                    expected_finish: payload.expected_finish,
                    revised_expected_finish: payload.revised_expected_finish ?? p.revised_expected_finish,
                    comments: payload.notes ?? p.comments,
                  }
                : p),
          };
        }),
      };
    });
    try {
      await axios.post(`${API}/projects/${payload.project_id}/schedules`, {
        week: payload.week,
        allocated_teams: 1,
        allocated_team_days: (payload.planned_workers * payload.working_days) / Math.max(1, payload.planned_workers),
        planned_workers: payload.planned_workers,
        working_days: payload.working_days,
        total_hours: payload.total_hours,
        expected_start: payload.expected_start,
        expected_finish: payload.expected_finish,
        revised_expected_finish: payload.revised_expected_finish,
        assigned_team_ids: payload.assigned_team_ids,
        is_tentative: !!payload.is_tentative,
        notes: payload.notes,
      }, authHeader());
      toast.success(`Updated ${payload.week}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      load();
      throw e;
    }
  };

  const saveProgress = async (projectId, percent) => {
    // Optimistic in-place update — no full re-fetch (avoids page "jump").
    setWeeksData((cur) => {
      if (!cur) return cur;
      return {
        ...cur,
        weeks: cur.weeks.map((w) => ({
          ...w,
          projects: (w.projects || []).map((p) =>
            p.project_id === projectId ? { ...p, progress_percent: percent } : p),
        })),
      };
    });
    try {
      const { data } = await axios.patch(`${API}/projects/${projectId}/progress`,
        { progress_percent: percent }, authHeader());
      if (data.auto_completed) {
        // Status changed server-side → sync locally
        setWeeksData((cur) => {
          if (!cur) return cur;
          return {
            ...cur,
            weeks: cur.weeks.map((w) => ({
              ...w,
              projects: (w.projects || []).map((p) =>
                p.project_id === projectId ? { ...p, status: "completed" } : p),
            })),
          };
        });
        toast.success("Project 100% → marked completed");
      } else {
        toast.success(`Progress: ${percent}%`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Progress update failed");
      load(); // rollback via re-fetch
      throw e;
    }
  };

  const saveStatus = async (projectId, newStatus) => {
    setWeeksData((cur) => {
      if (!cur) return cur;
      return {
        ...cur,
        weeks: cur.weeks.map((w) => ({
          ...w,
          projects: (w.projects || []).map((p) =>
            p.project_id === projectId ? { ...p, status: newStatus } : p),
        })),
      };
    });
    try {
      await axios.patch(`${API}/projects/${projectId}/status`,
        { status: newStatus }, authHeader());
      toast.success(`Status: ${STATUS_META[newStatus]?.label || newStatus}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status update failed");
      load();
      throw e;
    }
  };

  const moveScheduleToWeek = async (scheduleId, targetWeek) => {
    try {
      const { data } = await axios.patch(`${API}/schedules/${scheduleId}/move`,
        { new_week: targetWeek }, authHeader());
      if (data.unchanged) return;
      toast.success(`Moved to ${targetWeek} (carry-over marked)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    }
  };

  const saveProjectField = async (projectId, patch) => {
    // Optimistic in-place update. Field name → schedule row property mapping:
    //   name → project_name, comments → comments, address → address
    const rowPatch = {};
    if ("name" in patch) rowPatch.project_name = patch.name;
    if ("comments" in patch) rowPatch.comments = patch.comments;
    if ("address" in patch) rowPatch.address = patch.address;
    setWeeksData((cur) => {
      if (!cur) return cur;
      return {
        ...cur,
        weeks: cur.weeks.map((w) => ({
          ...w,
          projects: (w.projects || []).map((p) =>
            p.project_id === projectId ? { ...p, ...rowPatch } : p),
        })),
      };
    });
    try {
      await axios.patch(`${API}/projects/${projectId}/fields`, patch, authHeader());
      const key = Object.keys(patch)[0] || "field";
      toast.success(`Updated ${key}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      load();
      throw e;
    }
  };

  /** Persist a new order for a week and update state optimistically (no full reload). */
  const persistReorder = async (week, orderedIds) => {
    // Optimistic: mutate sort_order locally so the UI updates instantly.
    setWeeksData((cur) => {
      if (!cur) return cur;
      return {
        ...cur,
        weeks: cur.weeks.map((w) => {
          if (w.week !== week) return w;
          const rank = new Map(orderedIds.map((id, i) => [id, i]));
          const projects = (w.projects || []).map((p) => {
            if (p.is_shadow) return p;
            const r = rank.get(p.schedule_id);
            return r == null ? p : { ...p, sort_order: r };
          });
          return { ...w, projects };
        }),
      };
    });
    try {
      await axios.post(`${API}/schedules/reorder`, { week, ordered_schedule_ids: orderedIds }, authHeader());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
      // Roll back by re-fetching so state stays consistent with server truth
      load();
    }
  };

  const reorderInWeek = async (scheduleId, week, direction) => {
    const wk = weeksData?.weeks?.find((w) => w.week === week);
    if (!wk) return;
    const ids = (wk.projects || [])
      .filter((p) => !p.is_shadow)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .map((p) => p.schedule_id);
    const idx = ids.indexOf(scheduleId);
    if (idx < 0) return;
    const swapWith = direction === "up" ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= ids.length) return;
    [ids[idx], ids[swapWith]] = [ids[swapWith], ids[idx]];
    await persistReorder(week, ids);
  };

  /** DnD reorder: place `draggedId` immediately before `targetId` in the same week. */
  const reorderDropInWeek = async (draggedId, targetId, week) => {
    if (!draggedId || !targetId || draggedId === targetId) return;
    const wk = weeksData?.weeks?.find((w) => w.week === week);
    if (!wk) return;
    const ids = (wk.projects || [])
      .filter((p) => !p.is_shadow)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .map((p) => p.schedule_id);
    const from = ids.indexOf(draggedId);
    if (from < 0) return;
    ids.splice(from, 1);
    const to = ids.indexOf(targetId);
    if (to < 0) return;
    ids.splice(to, 0, draggedId);
    await persistReorder(week, ids);
  };

  // ==========================================================================
  // Clipboard: Ctrl+C / Ctrl+V / Ctrl+X for rows (Smartsheet-style)
  // Row must be selected (click) first. Ignored while typing in inputs.
  // ==========================================================================
  const copyRowById = (scheduleId) => {
    const row = allRows.find((r) => r.schedule_id === scheduleId && !r.is_shadow);
    if (!row) return false;
    setClipboardRow({ row, cut: false });
    toast.success(`Copied "${row.project_name}"`);
    return true;
  };
  const cutRowById = (scheduleId) => {
    const row = allRows.find((r) => r.schedule_id === scheduleId && !r.is_shadow);
    if (!row) return false;
    setClipboardRow({ row, cut: true, scheduleId });
    toast.success(`Cut "${row.project_name}" — paste (Ctrl+V) to move`);
    return true;
  };
  const pasteRow = () => {
    if (!clipboardRow) { toast.error("Clipboard empty — copy a row first"); return; }
    const src = clipboardRow.row;
    // Target week: selected row's week, else current filter, else current week
    let targetWeek = null;
    if (selectedRowId) {
      const sel = allRows.find((r) => r.schedule_id === selectedRowId);
      targetWeek = sel?.week;
    }
    targetWeek = targetWeek || selectedWeek || weeksData?.current_week;
    // Compute Monday of target week to pre-fill dates
    let mondayIso = "";
    const m = /^(\d{4})-W(\d{2})$/.exec(targetWeek || "");
    if (m) {
      const y = parseInt(m[1], 10), wk = parseInt(m[2], 10);
      const jan4 = new Date(Date.UTC(y, 0, 4));
      const jan4Day = jan4.getUTCDay() || 7;
      const monday = new Date(jan4);
      monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1 + (wk - 1) * 7);
      mondayIso = monday.toISOString().slice(0, 10);
    }
    const draft = {
      id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      week: targetWeek,
      task_name: src.project_name || "",
      address: src.address || "",
      expected_start: mondayIso,
      expected_finish: "",
      planned_workers: src.planned_workers || 4,
      working_days: src.working_days || 5,
      status: src.status || "not_started",
      assigned_team: src.assigned_team || "",
      comments: src.comments || "",
    };
    setDraftRows((cur) => [draft, ...cur]);
    toast.success(`Pasted into ${targetWeek}`);
    // If cut, delete original schedule (fire and forget)
    if (clipboardRow.cut && clipboardRow.scheduleId) {
      axios.delete(`${API}/schedules/${clipboardRow.scheduleId}`, authHeader())
        .then(() => load())
        .catch(() => toast.error("Failed to remove original after cut"));
      setClipboardRow(null);
    }
  };

  // Global keydown listener — only when a row is selected and user is not typing.
  useEffect(() => {
    const isTyping = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
    };
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (isTyping()) return;
      const k = e.key.toLowerCase();
      if (k === "c" && selectedRowId) {
        if (copyRowById(selectedRowId)) e.preventDefault();
      } else if (k === "x" && selectedRowId) {
        if (cutRowById(selectedRowId)) e.preventDefault();
      } else if (k === "v" && clipboardRow) {
        pasteRow();
        e.preventDefault();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selectedRowId, clipboardRow, allRows, weeksData, selectedWeek]);

  // Permission-aware read-only mode: if user only has "view", hide the header edit buttons.
  const { can } = usePermissions();
  const canEdit = can("admin_capacity", "edit");
  const isMobile = useIsMobile();

  return (
    <div className={`min-h-screen text-slate-100 theme-${theme}`}
         style={{ background: theme === "dark"
           ? "linear-gradient(180deg,#0a1628 0%,#050a14 100%)"
           : "linear-gradient(180deg,#f8fafc 0%,#eef2f7 100%)" }}>
      <style>{CAPACITY_THEME_CSS}</style>
      <DoserneTopNav />
      {!canEdit && (
        <div className="bg-amber-500/10 border-b border-amber-400/30 text-amber-200 text-xs font-black uppercase tracking-widest text-center py-2"
             data-testid="capacity-readonly-banner">
          🔒 Read-only mode — you do not have edit permission for this page
        </div>
      )}
      {/* ========== Header ========== */}
      <header className="sticky top-14 z-30 border-b border-white/5 bg-[#0a1628]/95 backdrop-blur-lg">
        <div className="max-w-[1600px] mx-auto px-4 h-16 flex items-center gap-3">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <Link to="/doserne"
                  className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center shadow-lg shadow-cyan-500/30 hover:scale-105 transition-transform"
                  title="Back to Doserne Academy"
                  data-testid="btn-back-to-doserne">
              <Zap className="w-5 h-5 text-slate-900" strokeWidth={3}/>
            </Link>
            <div className="min-w-0">
              <Link to="/doserne"
                    className="text-[10px] uppercase tracking-[0.3em] text-cyan-400 font-black hover:text-cyan-300 inline-flex items-center gap-1"
                    data-testid="btn-back-to-doserne-label">
                <ChevronLeft className="w-3 h-3"/> Doserne · Admin
              </Link>
              <div className="text-lg font-black tracking-tight leading-tight truncate">Team & Capacity Planner</div>
            </div>
          </div>
          <button onClick={() => setShowImport(true)}
                  className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-xs font-black uppercase"
                  data-testid="btn-import">
            <Upload className="w-3.5 h-3.5"/> Import CSV
          </button>
          <button onClick={() => setShowAbsences(true)}
                  className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-xs font-black uppercase"
                  data-testid="btn-absences"
                  title="Manage worker holidays / sick leave / training">
            <Umbrella className="w-3.5 h-3.5"/> Absences
          </button>
          <button onClick={() => {
                    const el = document.querySelector('[data-testid^="week-group-header-"][data-current="true"]');
                    if (el) {
                      el.scrollIntoView({ block: "center", behavior: "smooth" });
                    } else {
                      toast("No current-week row in view — clear the week filter first");
                    }
                  }}
                  className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/10 text-xs font-black uppercase"
                  data-testid="btn-jump-current"
                  title="Scroll the table to the current week">
            <Target className="w-3.5 h-3.5"/> Jump to current
          </button>
          <button onClick={() => setShowTeams(true)}
                  className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-xs font-black uppercase"
                  data-testid="btn-teams">
            <Users className="w-3.5 h-3.5"/> Teams ({teams.length})
          </button>
          <a href="/admin/project-overview"
             className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/10 text-xs font-black uppercase"
             data-testid="btn-project-overview"
             title="Smartsheet Project Overview (sync)">
            <RefreshCw className="w-3.5 h-3.5"/> Smartsheet
          </a>
          <button onClick={toggleTheme}
                  className="p-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5"
                  data-testid="btn-theme"
                  title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}>
            {theme === "dark" ? <Sun className="w-4 h-4"/> : <Moon className="w-4 h-4"/>}
          </button>
          <button onClick={() => setShowSettings(true)}
                  className="p-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5"
                  data-testid="btn-settings">
            <Settings2 className="w-4 h-4"/>
          </button>
          <button onClick={() => setWizardInitial({})}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-cyan-400 text-slate-900 text-xs font-black uppercase shadow-lg shadow-cyan-500/30"
                  data-testid="btn-new-project">
            <Plus className="w-4 h-4"/> New project
          </button>
        </div>
      </header>

      {/* ========== Sticky capacity ribbon ========== */}
      <CapacityRibbon
        weeksData={weeksData} weekCount={weekCount} setWeekCount={setWeekCount}
        startWeek={startWeek} setStartWeek={setStartWeek}
        selectedWeek={selectedWeek} setSelectedWeek={setSelectedWeek}
        mode={mode} setMode={setMode}
        fullYear={fullYear} setFullYear={setFullYear}
        onAdjustWeek={(w) => setOverrideWeek(w)}
      />

      {/* ========== Body: filters + table ========== */}
      <div className="max-w-[1600px] mx-auto p-4 space-y-3">
        {/* Filter bar */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 flex-1 max-w-md">
            <Filter className="w-3.5 h-3.5 text-slate-400"/>
            <input placeholder="Search task, client, address, comments..."
                   value={searchText}
                   onChange={(e) => setSearchText(e.target.value)}
                   className="bg-transparent border-0 outline-none text-sm text-slate-100 placeholder-slate-500 flex-1"
                   data-testid="filter-search"/>
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className={`${inputCls} max-w-[180px] text-xs`}
                  data-testid="filter-status">
            <option value="">All statuses</option>
            {statusOptions(statusFilter).map((s) => (
              <option key={s} value={s}>{STATUS_META[s].label}</option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 cursor-pointer">
            <input type="checkbox" checked={showFinished} onChange={(e) => setShowFinished(e.target.checked)} data-testid="toggle-finished"/>
            Show finished
          </label>
          {selectedWeek && (
            <button onClick={() => setSelectedWeek(null)}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-cyan-500/20 border border-cyan-400/40 text-cyan-200 text-xs font-black uppercase"
                    data-testid="clear-week-filter">
              <X className="w-3 h-3"/> {selectedWeek}
            </button>
          )}
          <button onClick={insertEmptyRow}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/30 text-xs font-black uppercase"
                  data-testid="btn-insert-row">
            <ListPlus className="w-3.5 h-3.5"/> Insert row
          </button>
          <div className="ml-auto flex items-center gap-2 text-[10px] text-slate-500">
            <div className="relative">
              <button type="button" onClick={() => setColMenuOpen((v) => !v)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 uppercase tracking-widest font-black"
                      data-testid="btn-columns-menu"
                      title="Show or hide columns">
                <Columns3 className="w-3.5 h-3.5"/> Columns
                {hiddenColumns.length > 0 && (
                  <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-200 text-[9px] font-black">
                    {hiddenColumns.length} hidden
                  </span>
                )}
              </button>
              {colMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setColMenuOpen(false)} data-testid="columns-menu-backdrop"/>
                  <div className="absolute right-0 mt-1 z-50 w-56 rounded-xl border border-white/10 bg-[#0a1628] shadow-2xl p-1.5"
                       data-testid="columns-menu">
                    <div className="flex items-center justify-between px-2 py-1.5">
                      <span className="text-[10px] uppercase tracking-widest font-black text-slate-400">Visible columns</span>
                      <button type="button" onClick={showAllColumns}
                              className="text-[10px] uppercase tracking-widest font-black text-cyan-300 hover:text-cyan-200 disabled:opacity-40"
                              disabled={hiddenColumns.length === 0}
                              data-testid="btn-show-all-columns">
                        Show all
                      </button>
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {columnOrder.map((id) => {
                        const col = TASK_COLUMN_DEFS[id];
                        if (!col) return null;
                        const frozen = !!col.frozen;
                        const visible = !hiddenColumns.includes(id);
                        return (
                          <button key={id} type="button"
                                  onClick={() => toggleColumn(id)}
                                  disabled={frozen}
                                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-xs font-bold ${frozen ? "opacity-50 cursor-not-allowed" : "hover:bg-white/5"} ${visible ? "text-slate-100" : "text-slate-500"}`}
                                  data-testid={`col-toggle-${id}`}>
                            <span className={`w-4 h-4 rounded flex items-center justify-center border ${visible ? "bg-cyan-500/20 border-cyan-400/50" : "border-white/15"}`}>
                              {visible && <Check className="w-3 h-3 text-cyan-300"/>}
                            </span>
                            <span className="flex-1 normal-case">{col.label}</span>
                            {frozen
                              ? <span className="text-[9px] uppercase tracking-widest text-slate-500">Locked</span>
                              : (visible ? <Eye className="w-3.5 h-3.5 text-slate-400"/> : <EyeOff className="w-3.5 h-3.5 text-slate-600"/>)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
            <span className="hidden sm:inline">Drag column headers to reorder</span>
            <button type="button" onClick={resetColumnOrder}
                    className="uppercase tracking-widest font-black text-slate-400 hover:text-cyan-300"
                    data-testid="btn-reset-columns"
                    title="Restore default column order">
              Reset
            </button>
            <span className="text-slate-600">·</span>
            <span>
              {filteredRows.length} row{filteredRows.length !== 1 ? "s" : ""} shown
              {unscheduledRows.length > 0 && ` · ${unscheduledRows.length} unscheduled`}
            </span>
          </div>
        </div>

        {/* Clipboard hint */}
        {clipboardRow && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-cyan-500/10 border border-cyan-400/30 text-cyan-100 text-xs"
               data-testid="clipboard-hint">
            <span className="uppercase tracking-widest font-black text-cyan-300">
              {clipboardRow.cut ? "Cut" : "Copied"}
            </span>
            <span className="font-mono truncate">{clipboardRow.row.project_name}</span>
            <span className="text-cyan-200/70 ml-auto">
              Press <kbd className="px-1 py-0.5 rounded bg-cyan-500/20 font-mono">Ctrl</kbd>+<kbd className="px-1 py-0.5 rounded bg-cyan-500/20 font-mono">V</kbd> to paste
            </span>
            <button onClick={() => setClipboardRow(null)}
                    className="p-1 rounded hover:bg-cyan-500/20"
                    title="Clear clipboard"
                    data-testid="clipboard-clear">
              <X className="w-3 h-3"/>
            </button>
          </div>
        )}

        {/* Alerts */}
        {weeksData?.alerts?.length > 0 && (
          <AlertsPanel alerts={weeksData.alerts}
                       onJumpToWeek={(week) => {
                         setSelectedWeek(week);
                         setTimeout(() => {
                           const el = document.querySelector(`[data-testid="week-group-header-${week}"]`);
                           if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
                         }, 50);
                       }}/>
        )}

        {/* Planning table */}
        {busy ? (
          <div className="rounded-2xl border border-white/10 bg-[#0f1a2e] p-12 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-400"/>
          </div>
        ) : isMobile ? (
          <MobilePlanningList rows={filteredRows}
                              drafts={draftRows.reduce((acc, d) => {
                                const w = d.week || "unscheduled";
                                acc[w] = acc[w] || [];
                                acc[w].push(d);
                                return acc;
                              }, {})}
                              onEditProject={editProjectById}
                              onCarryOver={openCarryOver}
                              onDeleteSchedule={deleteSchedule}
                              onSaveStatus={saveStatus}
                              onDraftChange={updateDraft}
                              onDraftSave={saveDraft}
                              onDraftDiscard={discardDraft}
                              onInsertRowForWeek={insertEmptyRow}
                              currentWeek={weeksData?.current_week}
                              currentWeekFrameColor={settings?.current_week_frame_color || "#10b981"}
                              statusColors={settings?.status_colors || {}}
                              emptyMsg={selectedWeek ? `No rows in ${selectedWeek}.` : null}/>
        ) : (
          <PlanningTable rows={filteredRows}
                         drafts={draftRows}
                         defaultWeek={selectedWeek || weeksData?.current_week}
                         onDraftChange={updateDraft}
                         onDraftSave={saveDraft}
                         onDraftDiscard={discardDraft}
                         onSaveAll={saveAllDrafts}
                         autoSave={autoSave}
                         onToggleAutoSave={setAutoSave}
                         bulkSaving={bulkSaving}
                         validDraftCount={validDraftCount}
                         onEditProject={editProjectById}
                         onCarryOver={openCarryOver}
                         onDeleteSchedule={deleteSchedule}
                         onQuickSaveSchedule={quickSaveSchedule}
                         onSaveProgress={saveProgress}
                         onSaveStatus={saveStatus}
                         onDropDraftOnWeek={moveDraftToWeek}
                         onMoveSchedule={moveScheduleToWeek}
                         onInsertRowForWeek={insertEmptyRow}
                         onSaveField={saveProjectField}
                         onReorderInWeek={reorderInWeek}
                         onReorderDropInWeek={reorderDropInWeek}
                         selectedRowId={selectedRowId}
                         onSelectRow={setSelectedRowId}
                         currentWeek={weeksData?.current_week}
                         currentWeekFrameColor={settings?.current_week_frame_color || "#10b981"}
                         columnOrder={columnOrder}
                         onReorderColumns={reorderColumns}
                         hiddenColumns={hiddenColumns}
                         statusColors={settings?.status_colors || {}}
                         emptyMsg={selectedWeek ? `No rows in ${selectedWeek}. Clear the filter or add projects.` : null}/>
        )}

        {/* Unscheduled panel */}
        {unscheduledRows.length > 0 && !selectedWeek && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-orange-300 font-black mb-2 flex items-center gap-1.5">
              <Package className="w-3 h-3"/> Unscheduled ({unscheduledRows.length}) — need a week
            </div>
            <PlanningTable rows={unscheduledRows}
                           groupByWeek={false}
                           onEditProject={editProjectById}
                           onCarryOver={openCarryOver}
                           onDeleteSchedule={() => {}}
                           onSaveProgress={saveProgress}
                           onSaveStatus={saveStatus}
                           onSaveField={saveProjectField}
                           columnOrder={columnOrder}
                           hiddenColumns={hiddenColumns}
                           statusColors={settings?.status_colors || {}}
                           emptyMsg=""/>
          </div>
        )}
      </div>

      {/* Modals */}
      {wizardInitial !== null && (
        <ProjectWizard initial={wizardInitial.id ? wizardInitial : null}
                       onClose={() => setWizardInitial(null)}
                       onSaved={() => { setWizardInitial(null); load(); }}/>
      )}
      {showSettings && settings && (
        <SettingsModal settings={settings}
                       onClose={() => setShowSettings(false)}
                       onSaved={() => { setShowSettings(false); load(); }}/>
      )}
      {showTeams && (
        <TeamsModal teams={teams} onClose={() => setShowTeams(false)} reload={load}/>
      )}
      {showImport && (
        <ImportModal onClose={() => setShowImport(false)}
                     onSaved={() => { setShowImport(false); load(); }}/>
      )}
      {carryOverProject && (
        <CarryOverModal project={carryOverProject}
                        onClose={() => setCarryOverProject(null)}
                        onSaved={() => { setCarryOverProject(null); load(); }}/>
      )}
      {showQuickAdd && (
        <QuickAddRowModal projects={projects}
                          defaultWeek={selectedWeek || weeksData?.current_week}
                          onClose={() => setShowQuickAdd(false)}
                          onSaved={() => { setShowQuickAdd(false); load(); }}/>
      )}
      {overrideWeek && (
        <WeekOverrideModal week={overrideWeek.week}
                           teams={teams.filter((t) => t.active)}
                           defaultWorkingDays={settings?.working_days_per_week || 5}
                           onClose={() => setOverrideWeek(null)}
                           onSaved={() => { setOverrideWeek(null); load(); }}/>
      )}
      {showAbsences && (
        <AbsencesModal onClose={() => setShowAbsences(false)}
                       onSaved={() => load()}/>
      )}
      <ChatFab />
    </div>
  );
}
